/**
 * TanStack Query hook for airport search
 */

import { useQuery } from '@tanstack/react-query'
import { searchAirports, getAirportDetails } from '@/lib/api/airports'
import { toAutocompleteItem } from '@/types/airports'
import type { AirportAutocompleteItem } from '@/types/airports'

/**
 * Query key factory for airport searches
 */
export const airportKeys = {
  all: ['airports'] as const,
  search: (query: string) => [...airportKeys.all, 'search', query] as const,
  details: (code: string) => [...airportKeys.all, 'details', code] as const,
}

/**
 * Hook to search airports with debounced query
 * Returns formatted autocomplete items
 *
 * @param query - Search string (min 2 characters)
 * @param enabled - Whether to run the query
 */
export function useAirportSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: airportKeys.search(query),
    queryFn: async (): Promise<AirportAutocompleteItem[]> => {
      const results = await searchAirports(query)
      return results.map(toAutocompleteItem)
    },
    enabled: enabled && query.length >= 2,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1,
  })
}

/**
 * Hook to get full airport details by code
 *
 * @param code - Airport code (ICAO or FAA)
 * @param enabled - Whether to run the query
 */
export function useAirportDetails(code: string | undefined, enabled = true) {
  return useQuery({
    queryKey: airportKeys.details(code ?? ''),
    queryFn: async () => {
      if (!code) return null
      const result = await getAirportDetails(code)
      return result ? toAutocompleteItem(result) : null
    },
    enabled: enabled && !!code,
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes (airport data rarely changes)
  })
}
